import type { Dispatch, ReactElement, ReactNode, SetStateAction } from "react"
import { createContext, useState } from "react"

import type { ModelSettingsSet } from "../../types/generated/api/ModelSettingsSet"
import type { CSVToJsonReturnType, ThresholdData } from "./Interfaces"

// eslint-disable-next-line react-refresh/only-export-components
export const Context = createContext<{
  currentThresholdMetricData: ThresholdData | undefined
  setCurrentThresholdMetricData: Dispatch<SetStateAction<ThresholdData | undefined>>
  thresholdMetricsData: CSVToJsonReturnType | undefined
  setThresholdMetricsData: Dispatch<SetStateAction<CSVToJsonReturnType | undefined>>
  selectedVersionNum: number | undefined
  setSelectedVersionNum: Dispatch<SetStateAction<number | undefined>>
  latestVersionNum: number | undefined
  setLatestVersionNum: Dispatch<SetStateAction<number | undefined>>
  configHistoryList: ModelSettingsSet[] | undefined
  setConfigHistoryList: Dispatch<SetStateAction<ModelSettingsSet[] | undefined>>
  currentConfigHistory: ModelSettingsSet | undefined
  setCurrentConfigHistory: Dispatch<SetStateAction<ModelSettingsSet | undefined>>
  isSidebarOpen: boolean
  setIsSidebarOpen: Dispatch<SetStateAction<boolean>>
}>({
  currentThresholdMetricData: undefined,
  setCurrentThresholdMetricData: () => undefined,
  thresholdMetricsData: undefined,
  setThresholdMetricsData: () => undefined,
  selectedVersionNum: undefined,
  setSelectedVersionNum: () => undefined,
  latestVersionNum: undefined,
  setLatestVersionNum: () => undefined,
  configHistoryList: undefined,
  setConfigHistoryList: () => undefined,
  currentConfigHistory: undefined,
  setCurrentConfigHistory: () => undefined,
  isSidebarOpen: false,
  setIsSidebarOpen: () => undefined,
})

/**
 * Provides a context for managing threshold metrics data in the application.
 * This context includes the current threshold metric data and the full
 * threshold metrics object, along with setters to update these values.
 *
 * @param {ReactNode} children - The components that will have access to the context.
 * @returns {ReactElement} A provider component that supplies the threshold metrics context to its children.
 */
export function ContextProvider({ children }: Readonly<{ children: ReactNode }>): ReactElement {
  const [thresholdMetricsData, setThresholdMetricsData] = useState<CSVToJsonReturnType>()

  const [currentThresholdMetricData, setCurrentThresholdMetricData] = useState<ThresholdData>()

  const [selectedVersionNum, setSelectedVersionNum] = useState<number>()
  const [latestVersionNum, setLatestVersionNum] = useState<number>()

  const [configHistoryList, setConfigHistoryList] = useState<ModelSettingsSet[]>()

  const [currentConfigHistory, setCurrentConfigHistory] = useState<ModelSettingsSet>()

  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  return (
    <Context.Provider
      value={{
        currentThresholdMetricData,
        setCurrentThresholdMetricData,
        thresholdMetricsData,
        setThresholdMetricsData,
        selectedVersionNum,
        setSelectedVersionNum,
        latestVersionNum,
        setLatestVersionNum,
        configHistoryList,
        setConfigHistoryList,
        currentConfigHistory,
        setCurrentConfigHistory,
        isSidebarOpen,
        setIsSidebarOpen,
      }}
    >
      {children}
    </Context.Provider>
  )
}
